import React from 'react';
import { StyleSheet, Text, View, TouchableOpacity, StyleProp, ViewStyle, TextStyle } from 'react-native';
import { MB_Modal } from '@/components/general/modal';
import { MB_Button } from '@/components/general/button';
import { mbApplyTransparency } from '@/utils/general/style-utils';
import { MB_SVG_Image, MB_SVG_Video } from '@/components/general/svg-components';
import { COLORS } from './MB_ResourcePickerConstants';

enum MB_RESOURCE_PICKER_TYPE_CALLBACK {
  camera = 'camera',
  gallery = 'gallery',
  dismissed = 'dismissed',
}

interface IMB_ResourcePickerCameraOrGalleryPopUp {
  /**
   * Whether the modal is visible.
   */
  isVisible: boolean;

  /**
   * The callback to call when an action happens.
   *  - The modal gets dismissed.
   *  - The user picks from camera.
   *  - The user picks from gallery.
   */
  callback: (callbackType: MB_RESOURCE_PICKER_TYPE_CALLBACK) => void;

  /**
   * The style of the modal's children.
   */
  modalChildrenStyle?: StyleProp<ViewStyle>;

  /**
   * Controls the color and size for the close button's X symbol.
   */
  modalCloseButtonXProps?: {
    /**
     * The size of the X symbol.
     */
    size?: number;

    /**
     * The color of the X symbol.
     */
    color?: string;
  };

  /**
   * The style of the camera or gallery button.
   */
  cameraOrGalleryButtonStyle?: StyleProp<ViewStyle>;

  /**
   * The style of the camera or gallery text.
   */
  cameraorGalleryTextStyle?: StyleProp<TextStyle>;

  /**
   * The color of the camera or gallery icon.
   */
  cameraorGalleryIconColor?: string;

  /**
   * The style of the cancel button.
   */
  cancelButtonStyle?: StyleProp<ViewStyle>;
}

const MB_ResourcePickerCameraOrGalleryPopUp = ({
  isVisible,
  callback,
  modalChildrenStyle,
  modalCloseButtonXProps,
  cameraOrGalleryButtonStyle,
  cameraorGalleryTextStyle,
  cameraorGalleryIconColor,
  cancelButtonStyle,
}: IMB_ResourcePickerCameraOrGalleryPopUp) => {
  return (
    <MB_Modal
      isVisible={isVisible}
      onDismiss={() => callback(MB_RESOURCE_PICKER_TYPE_CALLBACK.dismissed)}
      childrenWrapperStyle={modalChildrenStyle}
      closeButtonXProps={modalCloseButtonXProps}
    >
      <View style={styles.cameraOrGalleryWrapper}>
        <TouchableOpacity style={[styles.cameraOrGalleryButton, cameraOrGalleryButtonStyle]} onPress={() => callback(MB_RESOURCE_PICKER_TYPE_CALLBACK.gallery)}>
          <MB_SVG_Image color={cameraorGalleryIconColor ?? COLORS.white} size={58} />
          <Text style={[styles.cameraorGalleryText, cameraorGalleryTextStyle]}>Gallery</Text>
        </TouchableOpacity>

        <TouchableOpacity style={[styles.cameraOrGalleryButton, cameraOrGalleryButtonStyle]} onPress={() => callback(MB_RESOURCE_PICKER_TYPE_CALLBACK.camera)}>
          <MB_SVG_Video color={cameraorGalleryIconColor ?? COLORS.white} size={58} />
          <Text style={[styles.cameraorGalleryText, cameraorGalleryTextStyle]}>Camera</Text>
        </TouchableOpacity>
      </View>

      <MB_Button title="Cancel" style={[styles.cancelButton, cancelButtonStyle]} onPress={() => callback(MB_RESOURCE_PICKER_TYPE_CALLBACK.dismissed)} />
    </MB_Modal>
  );
};

export { MB_ResourcePickerCameraOrGalleryPopUp, MB_RESOURCE_PICKER_TYPE_CALLBACK };

const styles = StyleSheet.create({
  cameraOrGalleryWrapper: {
    width: 298,
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'center',
    justifyContent: 'space-between',
  },
  cameraOrGalleryButton: {
    width: 130,
    height: 130,
    backgroundColor: COLORS.darkPurple,
    borderRadius: 6,
    borderStyle: 'dashed',
    borderWidth: 1,
    borderColor: mbApplyTransparency(COLORS.dimViolet, 0.5),
    alignItems: 'center',
    justifyContent: 'center',
  },
  cameraorGalleryText: {
    //fontFamily: FONT_STYLES.PoppinsBase, TODO: Add font family
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    color: COLORS.dimViolet,
    marginTop: 12,
    position: 'absolute',
    bottom: 15,
  },
  cancelButton: {
    alignSelf: 'stretch',
    marginHorizontal: 20,
    maxWidth: 334,
    backgroundColor: COLORS.plum,
    marginTop: 42,
  },
});
